import { useEffect, useMemo, useState } from "react"
import { Download, Filter, FileText, Loader2, Users, Search, LogIn, LogOut, MapPin, ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Layout } from "@/components/Layout"
import { useAuth } from "@/hooks/useAuth"
import { useCondominium } from "@/hooks/useCondominium"
import { useVisitorsByDateRange } from "@/hooks/useVisitors"
import { generateVisitorReportPDF } from "@/utils/pdfGenerator"
import { format, differenceInMinutes, startOfMonth, subDays } from "date-fns"

const Relatorios = () => {
  const { profile } = useAuth()
  const { data: condominium } = useCondominium()

  const today = format(new Date(), "yyyy-MM-dd")
  const [startDate, setStartDate] = useState(today)
  const [endDate, setEndDate] = useState(today)
  const [searchDates, setSearchDates] = useState<{ start: string; end: string }>({ start: today, end: today })
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const { data: visitors = [], isLoading } = useVisitorsByDateRange(searchDates.start, searchDates.end)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 250)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, searchDates, pageSize])

  const filtered = useMemo(() => {
    if (!debouncedSearch) return visitors
    const q = debouncedSearch
    return visitors.filter((v: any) =>
      (v.name || "").toLowerCase().includes(q) ||
      (v.document || "").toLowerCase().includes(q) ||
      (v.notes || "").toLowerCase().includes(q) ||
      (v.authorized_by_user?.full_name || "").toLowerCase().includes(q)
    )
  }, [visitors, debouncedSearch])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginated = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize]
  )

  const stats = useMemo(() => {
    const entradas = filtered.length
    const saidas = filtered.filter((v: any) => v.exit_time).length
    const ativos = entradas - saidas
    const tempos = filtered
      .filter((v: any) => v.entry_time)
      .map((v: any) => differenceInMinutes(v.exit_time ? new Date(v.exit_time) : new Date(), new Date(v.entry_time)))
    const tempoMedio = tempos.length > 0 ? Math.round(tempos.reduce((a, b) => a + b, 0) / tempos.length) : 0
    return { entradas, saidas, ativos, tempoMedio }
  }, [filtered])

  const handleSearch = () => {
    if (!startDate || !endDate) {
      alert("Selecione o período")
      return
    }
    setSearchDates({ start: startDate, end: endDate })
  }

  const handleQuickFilter = (type: "today" | "week" | "month") => {
    const now = new Date()
    let start: string
    if (type === "today") start = today
    else if (type === "week") start = format(subDays(now, 7), "yyyy-MM-dd")
    else start = format(startOfMonth(now), "yyyy-MM-dd")
    setStartDate(start)
    setEndDate(today)
    setSearchDates({ start, end: today })
  }

  const handleDownloadPDF = async () => {
    const mapped = filtered.map((v: any) => ({
      name: v.name,
      document: v.document,
      destination: v.notes || v.unit?.number || null,
      entry_time: v.entry_time,
      exit_time: v.exit_time,
      created_at: v.created_at,
      status: v.status,
      registered_by: v.authorized_by_user?.full_name || null,
    }))
    await generateVisitorReportPDF(
      mapped,
      { name: condominium?.name || "Empresa", address: condominium?.address || undefined },
      searchDates.start,
      searchDates.end
    )
  }

  const formatDuration = (entryTime: string | null, exitTime: string | null) => {
    if (!entryTime) return "-"
    const end = exitTime ? new Date(exitTime) : new Date()
    const mins = differenceInMinutes(end, new Date(entryTime))
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return `${h}h${m.toString().padStart(2, "0")}min`
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Relatórios de Controle de Acessos</h1>
            <p className="text-gray-600">Histórico completo de entradas, saídas, destinos e responsáveis</p>
          </div>
          {filtered.length > 0 && (
            <Button onClick={handleDownloadPDF} className="bg-primary hover:bg-primary/90">
              <Download className="h-4 w-4 mr-2" />
              Baixar PDF
            </Button>
          )}
        </div>

        {/* Filtros */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-primary" />
              <span>Filtros</span>
            </CardTitle>
            <CardDescription>Selecione o período para consultar o histórico de acessos</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="data-inicio">Data Início</Label>
                <Input id="data-inicio" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data-fim">Data Fim</Label>
                <Input id="data-fim" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="busca">Buscar</Label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="busca"
                    placeholder="Nome, documento, destino, colaborador..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => handleQuickFilter("today")}>Hoje</Button>
              <Button variant="outline" size="sm" onClick={() => handleQuickFilter("week")}>Últimos 7 dias</Button>
              <Button variant="outline" size="sm" onClick={() => handleQuickFilter("month")}>Mês atual</Button>
              <Button onClick={handleSearch} className="ml-auto">
                <Filter className="h-4 w-4 mr-2" />
                Aplicar Filtro
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-full bg-blue-100 p-2"><LogIn className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.entradas}</p>
                <p className="text-xs text-muted-foreground">Entradas</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-full bg-emerald-100 p-2"><LogOut className="h-5 w-5 text-emerald-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.saidas}</p>
                <p className="text-xs text-muted-foreground">Saídas</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-full bg-amber-100 p-2"><Users className="h-5 w-5 text-amber-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.ativos}</p>
                <p className="text-xs text-muted-foreground">Ainda no Local</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-full bg-violet-100 p-2"><MapPin className="h-5 w-5 text-violet-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.tempoMedio}min</p>
                <p className="text-xs text-muted-foreground">Tempo Médio</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabela */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-primary" />
              <span>Registros do Período</span>
            </CardTitle>
            <CardDescription>
              {format(new Date(searchDates.start), "dd/MM/yyyy")} a {format(new Date(searchDates.end), "dd/MM/yyyy")}
              {" — "}
              {filtered.length} registro{filtered.length !== 1 ? "s" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500">Nenhum registro de acesso encontrado no período</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Visitante</TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead>Destino</TableHead>
                      <TableHead>Registrado por</TableHead>
                      <TableHead>Entrada</TableHead>
                      <TableHead>Saída</TableHead>
                      <TableHead>Permanência</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                  {paginated.map((v: any) => (
                      <TableRow key={v.id}>
                        <TableCell className="font-medium">{v.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{v.document || "—"}</TableCell>
                        <TableCell className="text-sm">{v.notes || v.unit?.number || "—"}</TableCell>
                        <TableCell className="text-sm">{v.authorized_by_user?.full_name || "—"}</TableCell>
                        <TableCell className="text-sm">
                          {v.entry_time ? format(new Date(v.entry_time), "dd/MM HH:mm") : "—"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {v.exit_time ? format(new Date(v.exit_time), "dd/MM HH:mm") : (
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Ativo</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{formatDuration(v.entry_time, v.exit_time)}</TableCell>
                        <TableCell>
                          {v.exit_time ? (
                            <Badge variant="secondary">Concluído</Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">No local</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
            </div>
          )}
          {!isLoading && filtered.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t mt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Linhas por página:</span>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className="h-8 w-[80px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[25, 50, 100, 200].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">
                  {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} de {filtered.length}
                </span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-2">Página {currentPage} de {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}

export default Relatorios
